"use client";

import {
  RUNTIME_OFFER_PLACEMENT_HI_SADE_SATI_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_SADE_SATI_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";

import { OfferPlacement } from "@/components/OfferPlacement";
import { pageReportBridgeOffer } from "@/components/OfferPlacement.page-bridge";
import RatingPrompt from "@/components/RatingPrompt";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { getCurrentTransits, type ActiveTransit } from "@/lib/api/transits";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { getTodayISTDateString } from "@/lib/ist-date";
import { getBrowserQueryClient } from "@/lib/query-client";
import { SADE_SATI_HI_HEADLINE, SADE_SATI_PAGE_HEADLINE } from "./SadeSati.copy";
import { SadeSatiPhases } from "./_phases";
import { RashiPicker } from "./_rashi-picker";
import SadeSatiPhaseMeter, {
  type SadeSatiData,
  type SadeSatiPhase,
} from "@/components/engine-visuals/SadeSatiPhaseMeter";

// Engine `phase` strings are free-form prose, so the meter derives its enum by
// keyword. The list is ORDERED — the first entry whose keywords appear wins —
// which preserves the original if/else-if precedence exactly.
const PHASE_KEYWORDS: ReadonlyArray<readonly [SadeSatiPhase, readonly string[]]> = [
  ["rising", ["rising", "first", "1"]],
  ["peak", ["peak", "second", "2"]],
  ["setting", ["setting", "third", "3"]],
  ["dhaiya_4th", ["4th", "kantaka"]],
  ["dhaiya_8th", ["8th", "ashtama"]],
];

function resolveSadeSatiPhase(rawPhase: string): SadeSatiPhase {
  const lower = rawPhase.toLowerCase();
  const matched = PHASE_KEYWORDS.find(([, keywords]) =>
    keywords.some((keyword) => lower.includes(keyword))
  );
  return matched ? matched[0] : "none";
}

export default function SadeSatiClient({
  defaultLanguage = "en",
  crossLinks,
}: {
  defaultLanguage?: "en" | "hi";
  crossLinks: ReactNode;
}) {
  const hi = defaultLanguage === "hi";
  const lookup = useSadeSatiLookup(defaultLanguage);

  const meterData: SadeSatiData | null = useMemo(() => {
    if (!lookup.selectedStatus || !lookup.selectedRashi) return null;
    const phase = resolveSadeSatiPhase(lookup.selectedStatus.phase || "");

    return {
      isActive: lookup.selectedStatus.is_active,
      phase,
      phaseTitleEn: lookup.selectedStatus.phase || "Active Transit",
      phaseTitleHi: lookup.selectedStatus.phase_description_hi || "सक्रिय गोचर",
      moonSignEn: lookup.selectedRashi,
      moonSignHi: lookup.selectedRashi,
      currentSaturnSignEn: lookup.saturnTransit?.current_rashi_name_en,
      currentSaturnSignHi: lookup.saturnTransit?.current_rashi_name_hi,
      impactSummaryEn: lookup.selectedStatus.phase_description_en,
      impactSummaryHi: lookup.selectedStatus.phase_description_hi,
      remediesEn: [
        "Recite Hanuman Chalisa daily",
        "Light a mustard oil lamp on Saturdays near a Peepal tree",
      ],
      remediesHi: [
        "प्रतिदिन श्री हनुमान चालीसा का पाठ करें",
        "शनिवार को पीपल के वृक्ष के समीप सरसों के तेल का दीपक जलाएं",
      ],
    };
  }, [lookup.selectedRashi, lookup.selectedStatus, lookup.saturnTransit]);

  return (
    <div className="intent-interface-diagnostic-first sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 sm:py-16">
        <SadeSatiHeader hi={hi} />
        <CurrentSaturnPosition
          saturnTransit={lookup.saturnTransit}
          calculationDate={lookup.dateStr}
          hi={hi}
        />
        <RashiPicker
          selectedRashi={lookup.selectedRashi}
          onSelect={lookup.setSelectedRashi}
          sadeSati={lookup.selectedStatus}
          saturnTransit={lookup.saturnTransit}
          selectedSaturn={lookup.selectedSaturn}
          isLoading={lookup.isLoading}
          language={defaultLanguage}
        />
        {meterData && (
          <div className="my-6">
            <SadeSatiPhaseMeter data={meterData} locale={hi ? "hi" : "en"} />
          </div>
        )}
        <SelectedResultOffer visible={lookup.hasSelectedResult} language={defaultLanguage} />
        <SelectedResultRating visible={lookup.hasSelectedResult} hi={hi} />

        <SadeSatiPhases locale={defaultLanguage} />
        {/* The Hindi page carries its own "साढ़े साती क्या है?" section, which
            also keeps this card's Moon-sign pointer (/hi/rashi-calculator/). */}
        {hi ? null : <SadeSatiSeoCard />}
        {crossLinks}
      </div>
    </div>
  );
}

function useSadeSatiLookup(language: "en" | "hi") {
  const [selectedRashi, setSelectedRashi] = useState<string | null>(null);
  const trackedRashiRef = useRef<string | null>(null);
  const dateStr = useMemo(() => getTodayISTDateString(), []);
  const queryRashi = selectedRashi ?? "mesh";
  const { data: transitData, isLoading } = useQuery(
    {
      queryKey: ["transits", "current", queryRashi, dateStr],
      queryFn: () => getCurrentTransits(queryRashi, dateStr),
      staleTime: 30 * 60 * 1000,
      retry: 1,
    },
    getBrowserQueryClient()
  );
  const saturnTransit = transitData?.active_transits.find((entry) => entry.planet_id === "saturn");
  const hasSelectedResult = Boolean(selectedRashi && transitData?.moon_rashi === selectedRashi);
  const selectedStatus = hasSelectedResult ? transitData?.sade_sati : undefined;
  // Saturn's house from the Moon (and so Dhaiya) differs per Moon sign: only the
  // reply for the picked sign may label it, never the default "mesh" reply.
  const selectedSaturn = hasSelectedResult ? saturnTransit : undefined;

  useEffect(() => {
    if (!selectedRashi || !hasSelectedResult || trackedRashiRef.current === selectedRashi) return;
    trackedRashiRef.current = selectedRashi;
    trackCalculatorUsed({
      calculator: "sade-sati",
      language,
      source_path: language === "hi" ? "/hi/sade-sati" : "/sade-sati",
    });
  }, [hasSelectedResult, language, selectedRashi]);

  return {
    dateStr,
    hasSelectedResult,
    isLoading,
    saturnTransit: saturnTransit ?? null,
    selectedRashi,
    selectedSaturn,
    selectedStatus,
    setSelectedRashi,
  };
}

function SelectedResultOffer({ visible, language }: { visible: boolean; language: "en" | "hi" }) {
  if (!visible) return null;
  const hi = language === "hi";
  return (
    <OfferPlacement
      {...pageReportBridgeOffer({
        source: hi ? "/hi/sade-sati" : "/sade-sati",
        planId: "sade_sati",
        intent: "remedy_dosha",
        placement: hi
          ? RUNTIME_OFFER_PLACEMENT_HI_SADE_SATI_REPORT_BRIDGE
          : RUNTIME_OFFER_PLACEMENT_SADE_SATI_REPORT_BRIDGE,
        language: language,
      })}
      headline={hi ? "अपनी साढ़े साती रिपोर्ट पाएँ" : "Get your personal Sade Sati Report"}
      subhead={
        hi
          ? "लाइव परिणाम चंद्र राशि से चरण दिखाता है। निजी रिपोर्ट में जन्म कुंडली का शनि, दशा, दबाव वाले क्षेत्र और आपकी कुंडली के हिसाब से उपाय मिलते हैं।"
          : "The live result checks your Moon-sign phase. The private report adds natal Shani, dasha context, pressure areas, and chart-specific upay."
      }
      ctaLabel={hi ? "साढ़े साती रिपोर्ट पाएँ" : "Get Sade Sati Report"}
    />
  );
}

function SelectedResultRating({ visible, hi }: { visible: boolean; hi: boolean }) {
  if (!visible) return null;
  return (
    <div className="mt-6">
      <RatingPrompt
        source={"tool_result" as never}
        mode="thumbs+reasons"
        reasons={RESULT_REASONS}
        prompt={hi ? "क्या यह साढ़े साती जाँच सही थी?" : "Was this Sade Sati check accurate?"}
        context={{
          surface: "sade_sati_result",
          path: typeof window !== "undefined" ? window.location.pathname : null,
        }}
      />
    </div>
  );
}

function SadeSatiHeader({ hi }: { hi: boolean }) {
  return (
    <div className="mb-10 text-center">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-intent-brand">
        {hi ? "शनि गोचर कैलकुलेटर" : "Saturn Transit Calculator"}
      </p>
      <h1 className="font-serif text-[28px] font-bold leading-tight text-ish-dark sm:text-[36px]">
        {hi ? SADE_SATI_HI_HEADLINE : SADE_SATI_PAGE_HEADLINE}
      </h1>
      <p className="mx-auto mt-3 max-w-lg text-body text-ish-text-muted">
        {hi
          ? "अपनी जन्म राशि को शनि की लाइव गणना की गई स्थिति से मिलाएँ।"
          : "Match your Moon sign against Shani's live engine-computed position."}
      </p>
    </div>
  );
}

function CurrentSaturnPosition({
  saturnTransit,
  calculationDate,
  hi,
}: {
  saturnTransit: ActiveTransit | null;
  calculationDate: string;
  hi: boolean;
}) {
  return (
    <div className="card-sacred mb-6 p-6 text-center sm:p-8">
      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-ish-text-faint">
        {hi ? "शनि की लाइव स्थिति" : "Live engine-computed Saturn position"}
      </span>
      {saturnTransit ? (
        <>
          <span className="block font-serif text-[28px] font-bold text-ish-dark">
            {hi ? (
              saturnTransit.current_rashi_name_hi
            ) : (
              <>
                {saturnTransit.current_rashi_name_en} ({saturnTransit.current_rashi_name_hi})
              </>
            )}
          </span>
          <span className="mt-1 block text-body-small text-ish-text-muted">
            ♄ {saturnTransit.rashi_degree.toFixed(1)}° · {saturnTransit.nakshatra} ·{" "}
            {saturnTransit.is_retrograde ? (hi ? "वक्री" : "Retrograde") : hi ? "मार्गी" : "Direct"}
          </span>
          <span className="mt-2 block text-caption text-ish-text-faint">{calculationDate}</span>
        </>
      ) : (
        <span className="text-body text-ish-text-muted">
          {hi ? "शनि की लाइव स्थिति की गणना हो रही है…" : "Calculating Saturn's live position…"}
        </span>
      )}
    </div>
  );
}

function SadeSatiSeoCard() {
  return (
    <div className="card-sacred mt-12 p-6 sm:p-8">
      <h2 className="mb-4 font-serif text-[20px] font-bold text-ish-dark">About Sade Sati</h2>
      <div className="space-y-3 text-body leading-relaxed text-ish-text-muted">
        <p>
          <strong className="text-ish-dark">Sade Sati</strong> is Saturn&apos;s transit through the
          12th, 1st, and 2nd signs counted from the natal Moon. The live engine result above uses
          your selected Janma Rashi instead of a year-specific hand-maintained status table.
        </p>
        <p>
          Don&apos;t know your Moon sign? Use the{" "}
          <Link href="/rashi-calculator/" className="font-semibold text-intent-brand">
            Rashi Calculator
          </Link>
          . For the deeper result, your personal Sade Sati Report combines the live transit with
          natal Saturn and the active dasha from your full birth chart.
        </p>
      </div>
    </div>
  );
}
